import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Snackbar, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import * as XLSX from "xlsx";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import ConnString from "../../ConnString.js";

const apiRoot = ConnString.connstring;
const MODULE_ID = "ACTCRP";
const storageKey = "cashReceipts.filters";
const validDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());
const apiDate = (date) => validDate(date) ? date.toLocaleDateString("en-US") : "";
const storedDate = (value, fallback) => { const date = value ? new Date(value) : fallback; return validDate(date) ? date : fallback; };
const branchCode = (branch) => String(branch?.Branch || branch?.BranchCode || branch?.id || "");
const branchName = (branch) => branch?.LocName || branch?.BranchName || branchCode(branch);
const escapeHtml = (value) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const TLCashReceipts = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dark = theme.palette.mode === "dark";
  const gridHeaderBackground = dark ? colors.blueAccent[700] : colors.blueAccent[200];
  const gridHeaderText = dark ? "#ffffff" : "#000000";
  const compID = sessionStorage.getItem("compID");
  const userID = sessionStorage.getItem("userID");
  const saved = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  }, []);
  const [dateFrom, setDateFrom] = useState(() => storedDate(saved.dateFrom, new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(() => storedDate(saved.dateTo, new Date()));
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [selection, setSelection] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // MUI X v8 requires the controlled selection model to contain an ids Set.
  const gridSelectionModel = useMemo(
    () => ({ type: "include", ids: new Set(selection) }),
    [selection]
  );

  const post = async (path, body) => {
    const response = await fetch(`${apiRoot}${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error("The server returned an invalid response."); }
    if (!response.ok || data.success === false) throw new Error(data.message || "Request failed.");
    return data;
  };

  const loadRows = useCallback(async (branch = selectedBranch, from = dateFrom, to = dateTo) => {
    const code = branchCode(branch);
    if (!code || !validDate(from) || !validDate(to) || from > to) return;
    try {
      setLoading(true);
      const response = await fetch(`${apiRoot}/api/TranslistData`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ compID, moduleID: MODULE_ID, transType: MODULE_ID, dateFrom: apiDate(from), dateTo: apiDate(to), filter: `and Branch = '${code.replace(/'/g, "''")}'` }),
      });
      const data = await response.json();
      setRows(Array.isArray(data.columns) ? data.columns.map((row, index) => ({ ...row, id: row.RecID ?? `${row.DocNo}-${index}` })) : []);
      setSelection([]);
      sessionStorage.setItem(storageKey, JSON.stringify({ dateFrom: from.toISOString(), dateTo: to.toISOString(), branch: code }));
    } catch (error) { setRows([]); setMessage({ severity: "error", text: error.message || "Unable to load Cash Receipts." }); }
    finally { setLoading(false); }
  }, [compID, dateFrom, dateTo, selectedBranch]);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      try {
        setLoading(true);
        const [header, branchData, accessData] = await Promise.all([
          post("/api/TranslistHeader", { compID, moduleID: MODULE_ID, transType: MODULE_ID, type: "ColumnNames" }),
          post("/api/userBranchAccess", { companyID: compID, currentuserID: userID }),
          post("/api/cash-receipts/access", { compID, userID }),
        ]);
        if (!active) return;
        setColumns((header.columns || []).map((field) => ({
          field, headerName: String(field).replace(/([a-z])([A-Z])/g, "$1 $2"), minWidth: Math.max(120, String(field).length * 10), flex: ["Particulars", "Customer", "Payor", "TradeName"].includes(field) ? 1 : undefined,
          renderCell: field === "Status" ? ({ value }) => { const status = String(value || "New"); return <Chip size="small" label={status} color={status.toUpperCase() === "CANCELLED" ? "error" : status.toUpperCase() === "POSTED" ? "success" : "warning"} variant="outlined" />; } : undefined,
        })));
        setPermissions(accessData.permissions || []);
        const options = branchData.options || [];
        setBranches(options);
        const initial = options.find((option) => branchCode(option) === saved.branch) || options[0] || null;
        setSelectedBranch(initial);
        if (initial) await loadRows(initial, storedDate(saved.dateFrom, dateFrom), storedDate(saved.dateTo, dateTo));
      } catch (error) { if (active) setMessage({ severity: "error", text: error.message }); }
      finally { if (active) setLoading(false); }
    };
    if (compID && userID) initialize();
    return () => { active = false; };
    // Initial values are intentionally restored once when the page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compID, userID]);

  const can = (action) => permissions.includes(action);
  const selectedRows = rows.filter((row) => selection.includes(String(row.RecID ?? row.id)) || selection.includes(row.RecID ?? row.id));
  const oneSelected = selectedRows.length === 1;
  const counts = useMemo(() => rows.reduce((result, row) => {
    const status = String(row.Status || "").toUpperCase(); result.total += 1;
    if (status === "POSTED") result.posted += 1; else if (status === "CANCELLED") result.cancelled += 1; else result.unposted += 1;
    return result;
  }, { total: 0, posted: 0, unposted: 0, cancelled: 0 }), [rows]);

  const openTransaction = (mode) => {
    if (mode !== "new" && !oneSelected) return setMessage({ severity: "warning", text: "Select exactly one Cash Receipt." });
    const row = selectedRows[0];
    const status = String(row?.Status || "").toUpperCase();
    if (mode === "edit" && ["POSTED", "CANCELLED"].includes(status)) return setMessage({ severity: "warning", text: `${row.DocNo || "This document"} is ${status.toLowerCase()} and cannot be edited.` });
    const params = new URLSearchParams({ mode, branch: branchCode(selectedBranch) });
    if (mode !== "new") params.set("recid", String(row.RecID));
    navigate(`/App/CashReceiptTransaction?${params.toString()}`);
  };

  const updateStatus = async (action) => {
    if (!selectedRows.length) return setMessage({ severity: "warning", text: "Select at least one Cash Receipt." });
    if (!window.confirm(`${action} ${selectedRows.length} selected Cash Receipt${selectedRows.length > 1 ? "s" : ""}?`)) return;
    try {
      setLoading(true);
      const data = await post("/api/cash-receipts/status", { compID, userID, action, recIDs: selectedRows.map((row) => row.RecID) });
      setMessage({ severity: "success", text: data.message }); await loadRows();
    } catch (error) { setMessage({ severity: "error", text: error.message }); }
    finally { setLoading(false); }
  };

  const exportExcel = () => {
    const visibleFields = columns.map((column) => column.field);
    const sheet = XLSX.utils.json_to_sheet(rows.map((row) => Object.fromEntries(visibleFields.map((field) => [field, row[field]]))));
    const book = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(book, sheet, "Cash Receipts");
    XLSX.writeFile(book, `Cash Receipts ${apiDate(dateFrom)} to ${apiDate(dateTo)}.xlsx`);
  };

  const printSelected = () => {
    if (!oneSelected) return setMessage({ severity: "warning", text: "Select exactly one Cash Receipt to print." });
    const row = selectedRows[0]; const popup = window.open("", "_blank");
    if (!popup) return setMessage({ severity: "warning", text: "Allow pop-ups to print the Cash Receipt." });
    popup.document.write(`<!doctype html><html><head><title>${escapeHtml(row.DocNo || "Cash Receipt")}</title><style>@page{size:A4 portrait;margin:15mm}body{font-family:Arial,sans-serif;color:#111}h2{margin-bottom:4px}.meta{color:#555;margin-bottom:20px}table{border-collapse:collapse;width:100%}td{border-bottom:1px solid #bbb;padding:7px}td:first-child{font-weight:700;width:32%}</style></head><body><h2>CASH RECEIPT</h2><div class="meta">${escapeHtml(row.DocNo || "")}</div><table>${columns.map((column) => `<tr><td>${escapeHtml(column.headerName)}</td><td>${escapeHtml(row[column.field])}</td></tr>`).join("")}</table><script>window.onload=()=>window.print()</script></body></html>`); popup.document.close();
  };

  const actions = [
    ["NEW", <AddCircleOutlineIcon />, () => openTransaction("new"), can("NEW")],
    ["CLONE", <ContentCopyOutlinedIcon />, () => openTransaction("clone"), can("CLONE") && oneSelected],
    ["EDIT", <EditOutlinedIcon />, () => openTransaction("edit"), can("EDIT") && oneSelected],
    ["VIEW", <VisibilityOutlinedIcon />, () => openTransaction("view"), can("VIEW") && oneSelected],
    ["CANCEL", <CancelOutlinedIcon />, () => updateStatus("CANCEL"), can("CANCEL") && selectedRows.length > 0],
    ["POST", <PostAddIcon />, () => updateStatus("POST"), can("POST") && selectedRows.length > 0],
    ["UNPOST", <UndoOutlinedIcon />, () => updateStatus("UNPOST"), can("UNPOST") && selectedRows.length > 0],
    ["PRINT", <PrintOutlinedIcon />, printSelected, can("PRINT") && oneSelected],
    ["EXTRACT", <DownloadOutlinedIcon />, exportExcel, can("EXTRACT") && rows.length > 0],
  ];

  return (
    <Box m={{ xs: 1.5, md: 3 }} display="flex" flexDirection="column" height="calc(100vh - 95px)" gap={2}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
        <Box><Typography variant="h3" fontWeight={800}>Cash Receipts</Typography><Typography color="text.secondary">{counts.total} records · {counts.unposted} unposted · {counts.posted} posted · {counts.cancelled} cancelled</Typography></Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Autocomplete size="small" sx={{ minWidth: 210 }} options={branches} value={selectedBranch} onChange={(_, value) => { setSelectedBranch(value); if (value) loadRows(value); }} getOptionLabel={(option) => branchName(option)} isOptionEqualToValue={(a, b) => branchCode(a) === branchCode(b)} renderInput={(params) => <TextField {...params} label="Branch" />} />
          <DatePicker label="From" value={dateFrom} onChange={setDateFrom} slotProps={{ textField: { size: "small" } }} />
          <DatePicker label="To" value={dateTo} onChange={setDateTo} slotProps={{ textField: { size: "small" } }} />
          <Button variant="contained" startIcon={<RefreshOutlinedIcon />} onClick={() => loadRows()}>Refresh</Button>
        </Stack>
      </Stack>
      <Paper elevation={0} sx={{ p: 1.25, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}><Stack direction="row" gap={1} flexWrap="wrap">{actions.map(([label, icon, handler, enabled]) => <Tooltip key={label} title={!enabled ? `${label} is unavailable` : label}><span><Button size="small" variant={label === "NEW" || label === "POST" ? "contained" : "outlined"} color={label === "CANCEL" || label === "UNPOST" ? "error" : label === "POST" ? "success" : "primary"} startIcon={icon} onClick={handler} disabled={loading || !enabled} sx={{ minWidth: 108, fontWeight: 700, ...(dark && { color: "common.white", borderColor: "currentColor" }), "& .MuiButton-startIcon": { color: "inherit" }, "&.Mui-disabled": { color: "action.disabled" } }}>{label}</Button></span></Tooltip>)}</Stack></Paper>
      <Paper elevation={0} sx={{ flex: 1, minHeight: 320, overflow: "hidden", borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <DataGrid rows={rows} columns={columns} loading={loading} checkboxSelection disableRowSelectionOnClick getRowId={(row) => row.RecID ?? row.id}
          rowSelectionModel={gridSelectionModel}
          onRowSelectionModelChange={(model) => setSelection(Array.from(model?.ids || []))}
          getRowClassName={({ row }) => `status-${String(row.Status || "").toLowerCase()}`}
          density="compact" pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ loadingOverlay: () => <Stack height="100%" alignItems="center" justifyContent="center"><CircularProgress /></Stack> }}
          sx={{ border: 0, "& .MuiDataGrid-columnHeaders": { bgcolor: gridHeaderBackground, color: gridHeaderText }, "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader .MuiSvgIcon-root, & .MuiDataGrid-menuIconButton": { color: `${gridHeaderText} !important` }, "& .status-posted": { bgcolor: dark ? "rgba(34,197,94,.08)" : "rgba(34,197,94,.05)" }, "& .status-cancelled": { opacity: .65 } }} />
      </Paper>
      <Snackbar open={!!message} autoHideDuration={5000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>{message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : undefined}</Snackbar>
    </Box>
  );
};

export default TLCashReceipts;
